import { Link } from 'react-router-dom'
import { money, formatDate } from '../format'
import { ORDER_LABELS, whereIsIt } from '../admin-orders'
import type { AdminOrderRow } from '../types'

export function AdminOrderTable({ orders, showCustomer = true }: { orders: AdminOrderRow[]; showCustomer?: boolean }) {
  return (
    <table className="admin-table">
      <thead>
        <tr>
          <th>Order</th>{showCustomer && <th>Customer</th>}<th>Where</th><th>Status</th>
          <th>Destination</th><th className="num">Charged</th><th className="num">Postage cost</th>
          <th className="num">After postage<br />&amp; refunds</th><th>Created</th>
        </tr>
      </thead>
      <tbody>
        {orders.map(order => {
          const place = whereIsIt(order)
          // What we actually kept: charged, less the carrier, less anything
          // returned. A partial refund is not a total loss.
          const margin = order.grossMinor - order.carrierCostMinor - order.refundedMinor
          return (
            <tr key={order.id}>
              <td>
                <Link className="admin-order-link" to={`/admin/orders/${order.id}`}>{order.publicId}</Link>
                {order.rxid && <code className="admin-rxid">{order.rxid}</code>}
                {order.refunded && <span className="admin-flag admin-flag--refund">{order.refundedMinor < order.grossMinor ? 'Partially refunded' : 'Refunded'}</span>}
                {order.archivedAt && <span className="admin-flag">Archived</span>}
                {order.cancellationPending && <span className="admin-flag">Cancellation pending</span>}
              </td>
              {showCustomer && (
                <td>
                  <Link className="admin-cust" to={`/admin/customers/${order.customerId}`}>{order.customerName || '—'}</Link>
                  <small>{order.customerEmail}</small>
                </td>
              )}
              <td><span className={`admin-where admin-where--${place.tone}`}>{place.label}</span></td>
              <td>{ORDER_LABELS[order.status] ?? order.status}</td>
              <td>{[order.destinationCity, order.destinationCountry].filter(Boolean).join(', ') || '—'}</td>
              <td className="num">{money(order.grossMinor, order.currency)}</td>
              <td className="num">{order.carrierCostMinor ? money(order.carrierCostMinor, order.currency) : '—'}</td>
              <td className={`num ${margin < 0 ? 'admin-neg' : ''}`}>
                {money(margin, order.currency)}
              </td>
              <td>{formatDate(order.createdAt)}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export function AdminTotalsStrip({ totals }: { totals: { count:number;authorizedCount:number;grossMinor:number;carrierCostMinor:number;marginMinor:number;refundedCount:number;refundedMinor:number } }) {
  return (
    <><div className="admin-totals" aria-label="Totals for the current filter">
      <div><small>Orders</small><b>{totals.count}</b><span>{totals.authorizedCount} authorized</span></div>
      <div><small>Live charges</small><b>{money(totals.grossMinor)}</b><span>both shipping legs</span></div>
      <div><small>Carrier cost</small><b>{money(totals.carrierCostMinor)}</b><span>labels bought</span></div>
      <div className={totals.marginMinor < 0 ? 'admin-totals__loss' : undefined}>
        <small>After postage &amp; refunds</small><b>{money(totals.marginMinor)}</b><span>before processing costs</span>
      </div>
      <div><small>Refunded</small><b>{money(totals.refundedMinor)}</b><span>{totals.refundedCount} order{totals.refundedCount === 1 ? '' : 's'}</span></div>
    </div><p className="admin-finance-note">USD · Test payments and staff authorizations excluded from charges. Only purchased postage is deducted. These are order totals, not a settlement report or net profit.</p></>
  )
}
